import { PaletteOptions } from '@mui/material/styles';
import { FontSize } from '@store/types/UserStoreSlice.type';
import colors from '@theme/colors';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle: true;
    body: true;
    bodySmall: true;
    buttonDark: true;
    tableHeader: true;
    badge: true;
  }
}

export const fontWeights = {
  default: 400,
  bold: 700,
  light: 300,
  semiBold: 600,
};

export const adjustFieldHeight = (height: number, fontSize: FontSize) => {
  switch (fontSize) {
    case FontSize.SMALL:
      return height - 2;
    case FontSize.LARGE:
      return height + 4;
    case FontSize.EXTRA_LARGE:
      return height + 6;
    default:
      return height;
  }
};

export const adjustLineHeight = (height: number, fontSize: FontSize) => {
  switch (fontSize) {
    case FontSize.SMALL:
      return height - 2;
    case FontSize.LARGE:
      return height + 4;
    case FontSize.EXTRA_LARGE:
      return height + 6;
    default:
      return height;
  }
};

export const adjustFontSize = (size: number, fontSize: FontSize) => {
  switch (fontSize) {
    case FontSize.SMALL:
      return size - 2;
    case FontSize.LARGE:
      return size + 2;
    case FontSize.EXTRA_LARGE:
      return size + 4;
    default:
      return size;
  }
};

// setting up for future :)
export const getTypographyVariants = (fontSize: FontSize) => {
  return {
    fontWeightLight: fontWeights.light,
    fontWeightRegular: fontWeights.default,
    fontWeightMedium: fontWeights.semiBold,
    fontWeightBold: fontWeights.bold,
    allVariants: {
      letterSpacing: 'normal',
      fontStyle: 'normal',
      fontWeight: fontWeights.semiBold,
      fontFamily: 'Proxima Nova',
    },
    root: {
      fontFamily: 'Proxima Nova',
      verticalAlign: 'middle',
    },
    h1: {
      fontSize: `${adjustFontSize(48, fontSize)}px`,
      lineHeight: `${adjustLineHeight(60, fontSize)}px`,
    },
    h2: {
      fontSize: `${adjustFontSize(40, fontSize)}px`,
      lineHeight: `${adjustLineHeight(48, fontSize)}px`,
      '@media screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(32, fontSize)}px`,
        lineHeight: `${adjustLineHeight(40, fontSize)}px`,
      },
    },
    h3: {
      fontSize: `${adjustFontSize(32, fontSize)}px`,
      lineHeight: `${adjustLineHeight(40, fontSize)}px`,
      '@media screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(24, fontSize)}px`,
        lineHeight: `${adjustLineHeight(32, fontSize)}px`,
      },
    },
    h4: {
      fontSize: `${adjustFontSize(24, fontSize)}px`,
      lineHeight: `${adjustLineHeight(32, fontSize)}px`,
      '@media screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(16, fontSize)}px`,
        lineHeight: `${adjustLineHeight(20, fontSize)}px`,
      },
    },
    h5: {
      fontSize: `${adjustFontSize(20, fontSize)}px`,
      lineHeight: `${adjustLineHeight(24, fontSize)}px`,
      '@media screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(14, fontSize)}px`,
        lineHeight: `${adjustLineHeight(20, fontSize)}px`,
      },
    },
    h6: {
      fontSize: `${adjustFontSize(16, fontSize)}px`,
      lineHeight: `${adjustLineHeight(20, fontSize)}px`,
    },
    subtitle: {
      fontSize: `${adjustFontSize(32, fontSize)}px`,
      lineHeight: `${adjustLineHeight(40, fontSize)}px`,
      fontWeight: fontWeights.default,
    },
    body: {
      fontSize: `${adjustFontSize(16, fontSize)}px`,
      lineHeight: `${adjustLineHeight(24, fontSize)}px`,
      fontWeight: fontWeights.default,
      '@media screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(14, fontSize)}px`,
      },
    },
    bodySmall: {
      fontSize: `${adjustFontSize(14, fontSize)}px`,
      lineHeight: `${adjustLineHeight(20, fontSize)}px`,
      fontWeight: fontWeights.default,
      '@media only screen and (maxWidth: 600px)': {
        fontSize: `${adjustFontSize(12, fontSize)}px`,
      },
    },
    caption: {
      fontSize: `${adjustFontSize(12, fontSize)}px`,
      lineHeight: `${adjustLineHeight(16, fontSize)}px`,
      fontWeight: fontWeights.default,
    },
    button: {
      fontSize: `${adjustFontSize(14, fontSize)}px`,
      lineHeight: `${adjustLineHeight(20, fontSize)}px`,
      cursor: 'pointer',
      textTransform: 'none',
      fontWeight: fontWeights.bold,
      fontStyle: 'normal',
      letterSpacing: 'normal',
    },
    tableHeader: {
      fontSize: `${adjustFontSize(10, fontSize)}px`,
      lineHeight: `${adjustLineHeight(12, fontSize)}px`,
      fontWeight: fontWeights.default,
      // color: colors.BLACK_60,
      textTransform: 'uppercase',
    },
    tableCell: {
      fontSize: `${adjustFontSize(14, fontSize)}px`,
      fontWeight: fontWeights.default,
    },
    badge: {
      fontSize: `${adjustFontSize(12, fontSize)}px`,
      lineHeight: `${adjustLineHeight(16, fontSize)}px`,
    },
    gridLink: {
      color: colors.SEAFOAM_SUPPLEMENTAL,
      lineHeight: '1',
    },
  };
};

export const MuiTypography = (palette: PaletteOptions) => ({
  defaultProps: {},
  styleOverrides: {},
  variants: [
    {
      style: {
        color: palette.text?.main,

        ':after': {
          color: palette.text?.main,
        },
      },
    },
    {
      props: { color: 'white60' },
      style: { color: colors.WHITE_60 },
    },
    {
      props: { color: 'white100' },
      style: { color: colors.WHITE_100 },
    },
    {
      props: { color: 'gray4' },
      style: { color: colors.MEDIUM_GRAY_4 },
    },
  ],
});
